import type { TaskRead } from "@/api/types";

/**
 * Build add-to-calendar URLs and ICS content for a scheduled task,
 * fully client-side.
 *
 * The original spec suggested fetching `GET /tasks/{id}/calendar-link`
 * from the backend, but every value the link needs (title, start,
 * end, location, description) is already on TaskRead — a server
 * round-trip would just stringify the same fields. Generating
 * locally also means there's no extra endpoint to gate behind
 * authentication and no flicker between "Add to Google Calendar"
 * appearing and being clickable.
 */

interface ScheduledRange {
  startUtc: Date;
  endUtc: Date;
}

function resolveRange(task: TaskRead): ScheduledRange | null {
  // Prefer the explicit scheduled_at for calls / meetings; fall back
  // to due_at so even a plain task can be exported as a 30-minute
  // block on the user's calendar.
  const startIso = task.scheduled_at ?? task.due_at;
  if (!startIso) return null;
  const startUtc = new Date(startIso);
  if (Number.isNaN(startUtc.getTime())) return null;
  const minutes = task.duration_minutes ?? 30;
  const endUtc = new Date(startUtc.getTime() + minutes * 60_000);
  return { startUtc, endUtc };
}

/** Compact UTC stamp used by both Google and ICS:
 *  20260510T180000Z */
function toCompactUtc(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

/** ISO without separators-in-date but with the extended time format —
 *  Outlook compose deeplink uses `2026-05-10T18:00:00Z`. */
function toOutlookIso(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

function buildDescription(task: TaskRead): string {
  const lines: string[] = [];
  if (task.description) lines.push(task.description.trim());
  if (task.company_name) lines.push(`Company: ${task.company_name}`);
  if (task.contact_name) {
    const email = task.contact_email ? ` <${task.contact_email}>` : "";
    lines.push(`Contact: ${task.contact_name}${email}`);
  }
  return lines.join("\n");
}

/** Shared input for the URL builders. Everything either builder needs
 *  in either flow (post-create toast vs in-dialog generator) lands
 *  here so the URLs stay consistent. */
export interface CalendarUrlInput {
  title: string;
  start: Date;
  end: Date;
  timezone?: string | null;
  location?: string | null;
  description?: string | null;
  /** Email addresses to invite. Google joins with ",", Outlook with
   *  ";" — both expect already-cleaned strings. */
  attendees?: ReadonlyArray<string>;
  /** Hint the calendar to attach a video meeting:
   *  - Google: `trp=true` so Google Calendar shows the "Add Google
   *    Meet" affordance pre-toggled. The actual Meet URL is minted
   *    when the user clicks Save inside Google.
   *  - Outlook: `online=true` (Office 365) which auto-attaches a
   *    Teams meeting on save.
   *  Use for type=call. Skip for type=meeting (physical) and the
   *  generic "Add to my calendar" flow. */
  includeMeet?: boolean;
}

const ATTENDEE_RE = /^[^\s,;]+@[^\s,;]+\.[^\s,;]+$/;
function cleanAttendees(input?: ReadonlyArray<string>): string[] {
  if (!input) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const trimmed = raw.trim();
    if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
    if (!ATTENDEE_RE.test(trimmed)) continue;
    seen.add(trimmed.toLowerCase());
    out.push(trimmed);
  }
  return out;
}

/** Build the deep-link the user opens to add the event to their
 *  calendar. Centralises the param vocabulary for both providers so
 *  consumers can't drift out of sync. */
export function buildAddToCalendarUrl(
  input: CalendarUrlInput,
  provider: "google" | "outlook",
): string {
  const attendees = cleanAttendees(input.attendees);
  if (provider === "google") {
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: input.title,
      dates: `${toCompactUtc(input.start)}/${toCompactUtc(input.end)}`,
    });
    if (input.timezone) params.set("ctz", input.timezone);
    if (input.description) params.set("details", input.description);
    if (input.location) params.set("location", input.location);
    if (attendees.length > 0) params.set("add", attendees.join(","));
    if (input.includeMeet) {
      // trp=true tells Google Calendar to prefer the "Make this a
      // Google Meet event" path. Once the user clicks Save the Meet
      // link is auto-attached and invitees receive it in the invite
      // email.
      params.set("trp", "true");
    }
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }
  // Outlook (Microsoft 365 web). The previous endpoint
  // (outlook.live.com/calendar/0/deeplink/compose) is the *personal*
  // Outlook flow; outlook.office.com works for business / 365
  // accounts which is what most B2B users in Prism are on.
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: input.title,
    startdt: toOutlookIso(input.start),
    enddt: toOutlookIso(input.end),
  });
  if (input.description) params.set("body", input.description);
  if (input.location) params.set("location", input.location);
  if (attendees.length > 0) params.set("to", attendees.join(";"));
  if (input.includeMeet) {
    // `online=true` flips the "Teams meeting" toggle on the
    // Outlook compose page. Outlook attaches the Teams link when
    // the user saves the event.
    params.set("online", "true");
  }
  return `https://outlook.office.com/calendar/0/action/compose?${params.toString()}`;
}

/** Convenience wrapper used by the post-create toast and detail
 *  sheet — builds the input from a saved TaskRead. */
export function googleCalendarUrl(task: TaskRead): string | null {
  const input = taskToCalendarInput(task);
  if (!input) return null;
  return buildAddToCalendarUrl(input, "google");
}

export function outlookCalendarUrl(task: TaskRead): string | null {
  const input = taskToCalendarInput(task);
  if (!input) return null;
  return buildAddToCalendarUrl(input, "outlook");
}

function taskToCalendarInput(task: TaskRead): CalendarUrlInput | null {
  const range = resolveRange(task);
  if (!range) return null;
  return {
    title: task.title,
    start: range.startUtc,
    end: range.endUtc,
    timezone: task.scheduled_timezone ?? undefined,
    location: task.location ?? undefined,
    description: buildDescription(task) || undefined,
    attendees: task.contact_email ? [task.contact_email] : undefined,
    // Calls auto-attach the Meet/Teams link when added to a calendar
    // post-creation; meetings (physical) don't.
    includeMeet: task.task_type === "call",
  };
}

/** Escape strings per RFC 5545: backslash, comma, semicolon, newline. */
function escapeIcs(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/** Build a self-contained ICS document the browser can save as a
 *  download. Single VEVENT — no recurring rules, alarms, or
 *  attendees, since the caller doesn't have RSVP scope. */
export function buildIcsContent(task: TaskRead): string | null {
  const range = resolveRange(task);
  if (!range) return null;
  const lines: string[] = [];
  lines.push("BEGIN:VCALENDAR");
  lines.push("VERSION:2.0");
  lines.push("PRODID:-//Prism CRM//EN");
  lines.push("CALSCALE:GREGORIAN");
  lines.push("METHOD:PUBLISH");
  lines.push("BEGIN:VEVENT");
  lines.push(`UID:prism-task-${task.id}@prism.local`);
  lines.push(`DTSTAMP:${toCompactUtc(new Date())}`);
  lines.push(`DTSTART:${toCompactUtc(range.startUtc)}`);
  lines.push(`DTEND:${toCompactUtc(range.endUtc)}`);
  lines.push(`SUMMARY:${escapeIcs(task.title)}`);
  const description = buildDescription(task);
  if (description) lines.push(`DESCRIPTION:${escapeIcs(description)}`);
  if (task.location) lines.push(`LOCATION:${escapeIcs(task.location)}`);
  lines.push("END:VEVENT");
  lines.push("END:VCALENDAR");
  // RFC 5545 requires CRLF line endings.
  return lines.join("\r\n");
}

/** Trigger a browser download of the task's ICS content. Used by the
 *  "Download .ics" button in the detail sheet. */
export function downloadIcs(task: TaskRead): boolean {
  const ics = buildIcsContent(task);
  if (!ics) return false;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slugifyForFilename(task.title)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation a tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}

function slugifyForFilename(title: string): string {
  return (
    title
      .replace(/[^\p{L}\p{N}\s-]/gu, "")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 80) || "event"
  );
}

/** True when the task carries enough metadata to export to a calendar.
 *  Used by the UI to gate the "Add to calendar" buttons. */
export function isSchedulable(task: TaskRead): boolean {
  return resolveRange(task) !== null;
}
